
import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useMutation, useQuery } from 'react-query';
import { createRecord, fetchRecord, fetchRecords, updateRecord, createEvent, deleteRecord, updateProperty, deleteProperty, createProperty, updateEvent } from 'api/actions/records';
import { fetcher } from 'api/client';
import ItemDetail from 'components/Shared/ItemDetail';
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import bootbox from 'components/Modals/Bootbox';
import DatePicker from 'components/Shared/DatePicker';
import {
    Row,
    Modal,
    ModalHeader,
    ModalBody,
    Card,
    Media,
    Container,
    Navbar,
    Button,
    Col,
    CardBody,
    CardTitle,
    CardSubtitle,
    CardGroup,
    FormGroup,
    Input,
    InputGroup,
    ListGroup,
    ListGroupItem,
    ListGroupItemText,
    ModalFooter,
    Form,
    Label,
    FormFeedback,
    InputGroupText,
    ButtonGroup
} from 'reactstrap';
import { useForm, Controller, FormProvider, useFormContext } from 'react-hook-form';
import { ConditionalItem } from '../MetadataView/SearchDetail'
import { useCancellablePromises, parseDate } from 'utils';
import { TenantMembersSelect } from '../ContractDetail/Contents/Collaborators/TenantMembersSelect';
import produce from 'immer'
import TratoLoader from 'components/Shared/TratoLoader';
import { MainHeader } from 'components/Shared/MainHeader';

const tabs = [
    {
        name: 'details',
        icon: 'folder',
        Component: Details
    },
    {
        name: 'properties',
        icon: 'cog',
        Component: Properties
    },
    {
        name: 'schedule',
        icon: 'clock',
        fullWidth: true,
        Component: Schedule
    },
    {
        name: 'collaborators',
        icon: 'users',
        Component: Collaborators
    },
];

const initialState = () => ({ name: '', recordId: '', hasChanges: false });

function Contract({ contract }) {
    return (
        <ListGroupItem>
            <Media>
                <Media>
                    <i className="fas fa-file p-2 text-primary"></i>
                </Media>
                <Media className="ml-2 align-self-center">
                    {contract?.name}
                </Media>
            </Media>
        </ListGroupItem>
    )
}

function Details({ state, setState, record }) {
    const { t } = useTranslation();

    const queryClient = useQueryClient();
    const { mutate: mutateDeleteRecord } = useMutation((recordId) => fetcher(deleteRecord(recordId)), {
        onSuccess: () => {
            queryClient.invalidateQueries('fetch-records');
            setState(initialState)
        }
    });

    const handleDelete = async () => {
        if (await (bootbox.confirm(t('records.confirm_delete'))))
            mutateDeleteRecord(state.recordId)
    }

    return (
        <ConnectForm>
            {({ register }) => (
                <div className="p-4 h-100">
                    <div style={{ height: 'calc(100% - 80px)' }}>
                        <FormGroup>
                            <label>{t('records.new.name')}:</label>
                            <input type="text" ref={register({ required: true })} className="form-control" name="name" placeholder={t('records.new.name_placeholder')} defaultValue={record?.name} />
                        </FormGroup>
                        <FormGroup>
                            <label>{t('records.new.description')}:</label>
                            <input type="text" ref={register} className="form-control" name="description" placeholder={t('records.new.description_placeholder')} defaultValue={record?.description} />
                        </FormGroup>
                        <label>{t('records.documents')}:</label>
                        <ListGroup>
                            {record?.contracts?.length ?
                                record.contracts.map((contract) =>
                                    (<Contract key={contract._id} contract={contract} />))
                                :
                                <ListGroupItem>
                                    <ListGroupItemText>{t('records.empty')}</ListGroupItemText>
                                </ListGroupItem>
                            }
                        </ListGroup>
                    </div>

                    <div className="mt-2">
                        <Button color="danger" outline block onClick={handleDelete}>
                            <i className="fas fa-times"></i> {t('records.delete')}
                        </Button>
                        <Button color="secondary" block type="submit">
                            <i className="fas fa-save text-white"></i> {t('records.new.save')}
                        </Button>
                    </div>
                </div>
            )}
        </ConnectForm>
    )
}

function Properties({ record, onSubmit }) {
    const { t } = useTranslation();
    const { errors, register, getValues } = useForm({ mode: 'all' });

    const queryClient = useQueryClient();
    const [isEditable, setIsEditable] = useState(false)

    const toggleIsEditable = (value) => {
        setIsEditable(value)
    }

    const handleSave = (prop) => {
        handleConditionChange({ id: prop, name: getValues(prop) })
        setIsEditable(false)
    }


    const handleConditionChange = (payload) => {
        onSubmit(
            produce(record, draft => {
                const index = record.properties.findIndex((p) => payload.id === p._id);
                if (payload.hasOwnProperty('name')) {
                    draft.properties[index].name = payload.name;
                }
                if (payload.hasOwnProperty('newName')) {
                    draft.properties[index].value = payload.newName;
                }
            }))
        // onSubmit({ _id: record._id, ...record, properties: record.properties.find((r) => (payload.id === r._id))?.value === payload.newName })
    }
    const handleDeleteCondition = async (payload) => {
        if (await (bootbox.confirm(t('records.confirm_delete_property'))))
            onSubmit(produce(record, draft => {
                draft.properties = record.properties.filter((p) => payload.id !== p._id);
            }))
        // onSubmit({ _id: record._id, ...record, properties: record.properties.find((r) => (payload.id === r._id))?.value === payload.newName })
    }
    // const handleDeleteCondition = (payload) => { onSubmit({ _id: record._id, }) }
    const onSuccess = useCallback(() => {
        queryClient.invalidateQueries(['fetch-record-paginated', { recordId: record._id, }])
    }, [record._id]);

    const { mutate: createPropertyMutation } = useMutation((payload) => fetcher(createProperty(payload)), { onSuccess });
    // const { mutate: propertyMutation } = useMutation((payload) => fetcher(updateProperty(payload)), { onSuccess });
    // const { mutate: deletePropertyMutation } = useMutation((payload) => fetcher(deleteProperty(payload)), { onSuccess });

    // const handleConditionChange = (payload) => propertyMutation({ recordId: record._id, propertyId: payload.id, value: payload.newName })
    // const handleDeleteCondition = async (payload) => {
    //     if (await (bootbox.confirm(t('records.confirm_delete_property'))))
    //         deletePropertyMutation({ recordId: record._id, propertyId: payload.id })
    // }
    const handleAddProperty = () => createPropertyMutation({ recordId: record._id });

    return (
        <div className="overflow-hidden d-flex flex-column h-100 p-4">
            <h5>{t('records.properties.title')}</h5>
            <div className="overflow-auto h-100">
                {record?.properties?.map((property) => (
                    <div className="mb-2 mr-4" key={property._id}>
                        {
                            (isEditable === property._id) ? (
                                <InputGroup>
                                    <Input
                                        innerRef={register({ required: true })}
                                        id={property._id}
                                        name={property._id}
                                        defaultValue={property?.name}
                                        autoFocus
                                        invalid={errors[property._id]}
                                    />
                                    <FormFeedback tooltip valid={!errors[property._id]}>El campo no puede estar vacío</FormFeedback>

                                    <Button size="sm" disabled={errors[property._id]} color="success" outline onClick={() => handleSave(property._id)}>
                                        <i className="fas fa-check" />
                                    </Button>
                                    <Button size="sm" color="danger" outline onClick={toggleIsEditable}>
                                        <i className="fas fa-times"></i>
                                    </Button>

                                </InputGroup>
                            ) : (
                                <div className="d-flex">
                                    <label>{property?.name} </label>
                                    <label className="ml-auto"><span onClick={() => toggleIsEditable(property._id)}> <i className="fas fa-edit text-info" title="Edit"></i> </span></label>
                                </div>
                            )
                        }
                        <ConditionalItem
                            key={property._id}
                            number={<i className="fas fa-file"></i>}
                            condition={{ ...property, id: property._id }}
                            onSave={handleConditionChange}
                            onDelete={handleDeleteCondition}
                        />
                    </div>
                ))}
            </div>
            <div>
                <Button block color="secondary" onClick={handleAddProperty}>
                    <i className="fas fa-plus"></i> {t('records.properties.add')}
                </Button>
            </div>
        </div>
    );
}

function Collaborators({ state, record, onSubmit }) {
    const { t } = useTranslation();
    const handleRemove = (userId) => {
        onSubmit(produce(record, draft => {
            draft.users = record.users.filter(u => u._id !== userId)
        }));
    }

    const handleCollaboratorChange = ({ label, value }) => {
        if (record?.users?.find(u => u._id === value)) return;
        onSubmit(produce(record, draft => {
            draft.users.push(value)
        }));
    }
    return (
        <div className="p-4">
            <div className="detail">
                <p>
                    {t('records.add_collaborators')}
                </p>
            </div>
            <div className="collaborators-search-wrapper">
                <div className="collaborators-toolbar">
                    <TenantMembersSelect
                        withGroups
                        contractId={'_'}
                        isDisabled={false}
                        onChange={handleCollaboratorChange}
                    />
                </div>
            </div>
            <div className="collaborators-toolbar-wrapper mt-3"></div>
            <ListGroup className="">
                {record?.users?.map((user, index) => index > 0 && (
                    <ListGroupItem key={user._id}>
                        <Media className="align-items-center">
                            <Media>
                                <img src="/images/detail/user-internal.png" />
                            </Media>
                            <Media body className="align-middle">
                                <span className="ml-1 text-muted">
                                    {user.givenName} {user.familyName}
                                </span>
                            </Media>
                            <Media onClick={() => handleRemove(user._id)}>
                                <i className="fas fa-times text-danger" ></i>
                            </Media>
                        </Media>
                    </ListGroupItem>
                ))}
            </ListGroup>
        </div>
    )
}

function NewEventModal({ state, setState }) {
    const { register, handleSubmit, control, reset } = useForm()
    const { t, i18n } = useTranslation();
    const queryClient = useQueryClient();
    const toggle = useCallback(() => {
        setState({ ...state, event: undefined })
    }, [setState, state])

    const onSuccess = useCallback(() => {
        queryClient.invalidateQueries('fetch-record-paginated')
        reset()
        toggle()
    }, [toggle])

    const { mutate: updateEventMutation } = useMutation((payload) => fetcher(updateEvent(state.recordId, payload)), {
        onSuccess
    })
    const { mutate: createEventMutation } = useMutation((payload) => fetcher(createEvent(state.recordId, payload)), {
        onSuccess
    });

    if (!state?.event) return null;

    const initialDate = state?.event?.date || state?.event?.dateStr;
    const endDate = state?.event?.date || state?.event?.dateEnd || state?.event?.dateStr;

    const doSubmit = (payload) => {
        state.event._id ?
            updateEventMutation({
                _id: state.event._id,
                startDate: payload.startDate || initialDate,
                endDate: payload.endDate || endDate,
                name: payload.name
            })
            :
            createEventMutation({
                startDate: payload.startDate || initialDate,
                endDate: payload.endDate || endDate,
                name: payload.name
            })
    }
    return (
        <Modal isOpen={state.event} centered>
            <ModalHeader toggle={toggle}>{t('records.events.create')}</ModalHeader>
            <Form onSubmit={handleSubmit(doSubmit)}>
                <ModalBody>
                    <FormGroup>
                        <FormGroup>
                            <Input
                                className="form-control mt-2 mb-3"
                                innerRef={register({ required: true })}
                                name="name"
                                defaultValue={state?.event?.name}
                                placeholder={t('records.events.name')}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('editor.documents.start_date')}:</Label>
                            <Controller
                                control={control}
                                name='startDate'
                                render={({ onChange, value }) => (
                                    <DatePicker
                                        locale={i18n.language}
                                        dateFormat="dd/MM/yyyy"
                                        className="form-control"
                                        selected={value || initialDate}
                                        onChange={onChange}
                                        defaultValue={initialDate}
                                        autoComplete={false}
                                        popperModifiers={{
                                            preventOverflow: {
                                                enabled: true,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('editor.notifications.endDate')}:</Label>
                            <Controller
                                control={control}
                                name='endDate'
                                render={({ onChange, value }) => (
                                    <DatePicker
                                        locale={i18n.language}
                                        dateFormat="dd/MM/yyyy"
                                        className="form-control"
                                        selected={value || endDate}
                                        onChange={onChange}
                                        defaultValue={endDate}
                                        autoComplete={false}
                                        popperModifiers={{
                                            preventOverflow: {
                                                enabled: true,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </FormGroup>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <ButtonGroup className="d-flex flex-grow-1">
                        <Button
                            color="light"
                            onClick={toggle}
                        >
                            {t('common.close')}
                        </Button>
                        <Button
                            color="primary"
                            type="submit">
                            {state?.event?._id ? t('records.events.update') : t('records.events.create')}
                        </Button>
                    </ButtonGroup>
                </ModalFooter>
            </Form>
        </Modal>
    )
}

function Schedule({ record, setState, state }) {
    const { t, i18n } = useTranslation();
    const handleDateClick = (event) => {
        setState({ ...state, event })
    }
    const [show, setShow] = useState(false);
    const calendar = useRef();
    const handleEventClick = (info) => {
        setState({
            ...state, event: {
                name: info.event.title,
                dateStr: info.event.start,
                dateEnd: info.event.end,
                _id: info.event.id
            }
        })
    }
    useEffect(() => {
        setTimeout(() => {
            setShow(true);
        }, 500);
    }, [])

    return (
        <div className="father-calendar" style={{ height: '100%' }}>
            {show ?
                <FullCalendar
                    height="100%"
                    ref={calendar}
                    timeZone={'local'}
                    locale={i18n.language.toLowerCase() ?? 'es'}
                    selectable={true}
                    // editable={true}
                    plugins={[dayGridPlugin, interactionPlugin, listPlugin]}
                    initialView="dayGridMonth"
                    dateClick={handleDateClick}
                    eventClick={handleEventClick}
                    noEventsContent={() => t('records.noevents')}
                    headerToolbar={{
                        right: 'prev,next today',
                        center: 'title',
                        left: 'dayGridMonth,listWeek'
                    }}
                    buttonText={{
                        today: t('common.today'),
                        month: t('common.month'),
                        week: t('common.week'),
                        day: t('common.day'),
                        dayGridMonth: t('common.dayGridMonth'),
                        listWeek: t('common.listWeek'),
                    }}
                    // eventResize={}
                    // eventDrop={}
                    events={record?.events?.map(
                        e =>
                        (
                            { id: e._id, title: e.name, start: new Date(e.startDate), end: new Date(e.endDate), allDay: true })

                        /* porque se usaba end: new Date(e.endDate || e.startDate) */
                    )
                    }
                /> : <TratoLoader className="absolute-center" size="lg" />}
        </div>
    )
}

function Record({ record, setState, methods, state }) {
    const { t } = useTranslation();
    const history = useHistory();
    const { name, contracts = [], _id, createdAt } = record;
    const api = useCancellablePromises();

    const handleSetRecordId = () => {
        methods.reset();
        setState({ name, recordId: _id })
    }
    const handleDoubleClick = () => {
        api.clearPendingPromises();
        history.push(`/records/list/contracts/${_id}`)
    };

    return (
        <Col md="4" className="mt-3 pointer" onClick={handleSetRecordId} onDoubleClick={handleDoubleClick}>
            <Card className={clsx('shadow-sm rounded-lg', state?.recordId === _id && 'shadow')} >
                <CardBody>
                    <Media>
                        <i className="fas fa-file text-muted fa-2x"></i>
                        <CardTitle tag="h5" className="text-break ml-3 text-truncate">{name}</CardTitle>
                    </Media>
                    <CardSubtitle className="text-muted mt-3"> {t('records.modified')}: {parseDate(createdAt, 'P')}</CardSubtitle>
                    <CardGroup className="text-center mt-3">
                        <Card className="border-0">
                            <h4 className="font-weight-bold">{contracts.length}</h4>
                            <small className="text-muted">{t('records.contract_number')}</small>
                        </Card>
                    </CardGroup>
                </CardBody>
            </Card>
        </Col>
    )
}

const ConnectForm = ({ children }) => {
    const methods = useFormContext();

    return children({ ...methods });
};

function Records() {
    const [state, setState] = useState(initialState);
    const handleClose = () => setState(initialState);
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { mutate: createRecordMutation } = useMutation((payload) => fetcher(createRecord(payload)), {
        onSuccess: () => {
            queryClient.invalidateQueries('fetch-records')
        }
    });
    const methods = useForm();

    const { data: records } = useQuery('fetch-records', () => fetcher(fetchRecords()))

    const {
        data,
        isFetching,
    } = useQuery(['fetch-record-paginated', { recordId: state.recordId, }], () => fetcher(fetchRecord(state.recordId)), { enabled: !!state.recordId, keepPreviousData: true })


    const handleCreateRecord = useCallback(() => {
        const properties = [
            t('records.new.type'),
            t('records.new.actor'),
            t('records.new.sued'),
            t('records.new.act'),
            t('records.new.organ'),
        ].map((p) => ({ name: p, value: '' }))
        createRecordMutation({ name: t('records.new.title'), properties })
    }, [createRecordMutation, t]);

    const { mutate: mutateRecord } = useMutation((payload) => fetcher(updateRecord(payload)), {
        onSuccess: (data) => {
            queryClient.invalidateQueries('fetch-records')
            queryClient.setQueryData(['fetch-record-paginated', { recordId: data._id, }], data)
        }
    });
    const onSubmit = data => {
        mutateRecord({ _id: state.recordId, ...data });
    }


    return (
        <>
            <MainHeader title="records.title">
                <Button color="white" onClick={handleCreateRecord} title={t('records.create_new')}>
                    <i className="fas fa-plus fa-2x text-primary"></i>
                </Button>
            </MainHeader>
            <Container fluid>
                <NewEventModal state={state} setState={setState} />
                <FormProvider {...methods}>

                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        <Row>
                            {records?.map((record) => (
                                <Record
                                    record={record}
                                    key={record._id}
                                    setState={setState}
                                    methods={methods}
                                    state={state}
                                />
                            ))}
                        </Row>
                        <ItemDetail
                            isOpen={state.recordId}
                            title={state.name}
                            onClose={handleClose}
                            tabs={tabs}
                            recordId={state.recordId}
                            setState={setState}
                            state={state}
                            record={data}
                            onSubmit={onSubmit}
                        />
                    </form>
                </FormProvider>
            </Container >
        </>
    )
}

export default Records;